import { Button, Divider, Link, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { CurrentTaskIcon } from "src/assets/icons";
import {
  ItemDescriptionField,
  ItemDrawerTitle,
  SignalRGroup,
  SnackbarContent,
  TaskItemContextProperty,
  TaskItemFeed,
  TaskItemProperties,
  useDrawer,
} from "src/components";
import { Links, TaskItemPropertyType } from "src/constants";
import { useSession, useSignalR } from "src/hooks";
import { queryKeys, useEditTaskItem, useEditTaskItemFocus, useGetTaskItemDetail, UserDto } from "src/http";
import { ITeamSummary } from "src/models";

interface IProps {
  taskItemGuid: string;
  onOwnershipChanged?: (taskItemGuid: string, taskItemDescription: string, removedOwners: UserDto[]) => void;
}

const TaskItemDrawerContent = (props: IProps) => {
  const { userId: sessionUserGuid } = useSession();
  const { isLoading, data: taskItem } = useGetTaskItemDetail(props.taskItemGuid);
  const { mutate: editTaskItem } = useEditTaskItem();
  const { mutate: editTaskItemFocus } = useEditTaskItemFocus();
  const signalR = useSignalR();
  const queryClient = useQueryClient();
  const { showDrawer, closeDrawer } = useDrawer();

  const userOwnership = taskItem?.owners.find((x) => x.user.userId === sessionUserGuid);

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.TaskItem(props.taskItemGuid));

    return () => {
      signalR.leaveGroup(SignalRGroup.TaskItem(props.taskItemGuid));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.taskItemGuid]);

  useEffect(() => {
    const handleShouldRefetchTaskItem = (taskItemGuid: string) => {
      queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemDetail(taskItemGuid));
    };

    signalR.onShouldRefetchTaskItem(handleShouldRefetchTaskItem);
    return () => signalR.offShouldRefetchTaskItem(handleShouldRefetchTaskItem);
  }, [queryClient, signalR]);

  function handleDescriptionChanged(value: string) {
    if (taskItem === undefined) {
      return;
    }

    editTaskItem({
      taskItemGuid: taskItem.guid,
      description: value,
    });
  }

  function handleFocusTaskClicked() {
    if (taskItem === undefined || userOwnership === undefined) {
      return;
    }

    editTaskItemFocus({
      taskItemGuid: taskItem.guid,
      isFocused: !userOwnership.isFocused,
      ownerGuid: sessionUserGuid,
    });
  }

  function handleTeamChanged(selectedTeam: ITeamSummary | null) {
    if (taskItem === undefined) {
      return;
    }

    if (!selectedTeam) {
      enqueueSnackbar(<SnackbarContent title="Task removed from team." />, {
        action: (
          <Button href={Links.TaskItemDetail(taskItem.guid)} sx={{ whiteSpace: "nowrap" }}>
            Open Page
          </Button>
        ),
      });
      closeDrawer();
    } else if (taskItem.team?.slug !== selectedTeam.slug) {
      enqueueSnackbar(
        <SnackbarContent
          title={
            <>
              <span>Task moved to </span>
              <Link onClick={(e) => e.stopPropagation()} href={Links.TeamImplementation(selectedTeam.slug)}>
                {selectedTeam.name}
              </Link>
            </>
          }
        />,
        {
          action: (
            <Button href={Links.TaskItemDetail(taskItem.guid)} sx={{ whiteSpace: "nowrap" }}>
              Open Page
            </Button>
          ),
        }
      );
      closeDrawer();
    }
  }

  function showTaskItemDrawer(taskItemGuid: string) {
    showDrawer({
      title: <ItemDrawerTitle link={Links.TaskItemDetail(taskItemGuid)} />,
      content: <TaskItemDrawerContent taskItemGuid={taskItemGuid} onOwnershipChanged={props.onOwnershipChanged} />,
    });
  }

  let shouldDisplayContextProperty =
    !isLoading && taskItem && (taskItem.context || taskItem.parentTaskItem || taskItem.userCanEdit);

  return (
    <>
      <Stack sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5 }}>
        {shouldDisplayContextProperty && taskItem && (
          <TaskItemContextProperty taskItem={taskItem} isEditable={taskItem.userCanEdit} linksOpenDrawer />
        )}

        <ItemDescriptionField
          isLoading={isLoading}
          isEditable={taskItem?.userCanEdit ?? false}
          value={taskItem?.description}
          onChange={handleDescriptionChanged}
          variant={"h5"}
        />

        {userOwnership && (
          <Stack spacing={0.5} sx={{ alignItems: "start", mt: 0.5 }}>
            <Divider variant="dashed" flexItem />
            <Button
              variant={userOwnership.isFocused ? "contained" : "tertiary"}
              startIcon={<CurrentTaskIcon />}
              onClick={handleFocusTaskClicked}
            >
              {userOwnership.isFocused ? "Task in Focus" : "Add to Tasks in Focus"}
            </Button>
          </Stack>
        )}
      </Stack>
      <Divider />
      <Stack divider={<Divider flexItem />} sx={{ overflow: "auto" }}>
        <TaskItemProperties
          taskItem={taskItem}
          isLoading={isLoading}
          propertyVisibility={{
            [TaskItemPropertyType.Notes]: true,
            [TaskItemPropertyType.Ownership]: true,
            [TaskItemPropertyType.Status]: true,
            [TaskItemPropertyType.DueDate]: true,
            [TaskItemPropertyType.Subtasks]: true,
          }}
          onTeamChanged={handleTeamChanged}
          onOwnershipChanged={props.onOwnershipChanged}
          onSubtaskClicked={showTaskItemDrawer}
        />

        <TaskItemFeed taskItemGuid={props.taskItemGuid} />
      </Stack>
    </>
  );
};

export { TaskItemDrawerContent };
